// Creates Sequential Workflow Designer instances with the app's configuration. The library is
// loaded on first use so it is its own chunk and never part of application.js.
import { toDrawing } from "./workflow_definition";

const svgIcon = (color, path) => `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${path}</svg>`
)}`;

const ICONS = {
  triage: svgIcon("#7c3aed", '<path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>'),
  transmit: svgIcon("#16a34a", '<path d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"/>'),
  parallel: svgIcon("#6b7280", '<path d="M12 3v4M6 7h12M6 7v4M18 7v4M12 7v4M6 15v2M12 15v2M18 15v2M6 21h12"/>')
};

let library = null;

export async function loadDesigner() {
  if (!library) library = await import("sequential-workflow-designer");
  return library;
}

const isDark = () => document.documentElement.classList.contains("dark");

const iconUrlProvider = (componentType, type) => ICONS[type] || ICONS.parallel;

// Loads the library, clears the container, draws the definition with the current theme and the
// app's icons, and fits the viewport once the library reports ready. The library builds its root
// component in a timeout after create, so resetViewport before onReady throws "Root component not
// found". `options` is the per-use configuration.
async function createDesigner(container, definition, options) {
  const SWD = await loadDesigner();
  container.replaceChildren();
  const designer = SWD.Designer.create(container, toDrawing(definition, () => SWD.Uid.next()), {
    theme: isDark() ? "dark" : "light",
    steps: { iconUrlProvider },
    ...options
  });
  designer.onReady.subscribe(() => designer.resetViewport());
  return designer;
}

// A drawing with no toolbox, editor, or control bar (Review, the channel drawer, the report page).
export function createReadonlyDesigner(container, definition) {
  return createDesigner(container, definition, { isReadonly: true, toolbox: false, editors: false, controlBar: false, contextMenu: false });
}

// The editor with the toolbox (collapsed), the control bar, the validator, and the step panel.
export function createEditableDesigner(container, definition, { toolboxGroups, validator, stepEditorProvider, rootEditorProvider }) {
  return createDesigner(container, definition, {
    undoStackSize: 20,
    validator: { step: validator },
    toolbox: { isCollapsed: true, groups: toolboxGroups },
    editors: { isCollapsed: false, rootEditorProvider, stepEditorProvider },
    controlBar: true,
    contextMenu: true
  });
}

// Colours the drawn steps by run state through the library's CSS classes: run-<status>.
export function applyStepStates(container, states) {
  container.querySelectorAll(".sqd-step-task").forEach((g) => {
    Array.from(g.classList).filter((c) => c.startsWith("run-")).forEach((c) => g.classList.remove(c));
    const name = g.querySelector(".sqd-step-task-text")?.textContent?.trim();
    const status = states && name ? states[name] : null;
    if (status) g.classList.add(`run-${status}`);
  });
}

// The toolbox entries: the five triage tasks, transmit, and the two blocks.
export function toolboxGroups(uid) {
  const task = (name, properties) => ({ id: uid(), componentType: "task", type: properties.kind, name, properties: withoutKind(properties) });
  const withoutKind = ({ kind, ...rest }) => rest;
  return [
    { name: "Triage", steps: [
      task("summary", { kind: "triage", task: "summary", engine: "ai" }),
      task("scope", { kind: "triage", task: "scope", engine: "rule" }),
      task("duplicate", { kind: "triage", task: "duplicate", engine: "ai", method: "embeddings", lookback_days: 90 }),
      task("validity", { kind: "triage", task: "validity", engine: "ai" }),
      task("severity", { kind: "triage", task: "severity", engine: "ai", scale: "standard" })
    ] },
    { name: "Transmit", steps: [task("transmit", { kind: "transmit", integration: { type: "", id: "" }, cloaked: "send" })] },
    { name: "Blocks", steps: [
      { id: uid(), componentType: "launchPad", type: "parallel", name: "parallel", properties: {}, sequence: [] },
      { id: uid(), componentType: "container", type: "lane", name: "lane", properties: {}, sequence: [] }
    ] }
  ];
}
